import React, { useCallback, useEffect, useState } from "react";
import {
	Flex,
	Text,
	Divider,
	Button,
	useDisclosure,
	Drawer,
	DrawerBody,
	DrawerContent,
	DrawerHeader,
	DrawerOverlay,
	DrawerCloseButton,
	DrawerFooter,
	Box,
} from "@chakra-ui/react";
import moment from "moment";
import { HStack, VStack } from "@chakra-ui/layout";
import type { ShiftItem } from "@models/Order";
import { getFormatDate, getFormatTime, getISOWeekString, getTimeDiffInHours, isValidTime } from "@utils/dateUtils";
import ShiftEditItem from "./ShiftEditItem";
import DateItem from "./DateItem";
import EmptyWhiteCircle from "@components/icons/EmptyWhiteCircle";
import CheckCircleIcon from "@components/icons/CheckCircleIcon";
import { getSwedishWeekDayName, applyCopiedScheduleForSelectedWeek } from "@utils/dateUtils";

interface Props {
	dateList: ShiftItem[];
	onChangeDateList: (tempDateItems: ShiftItem[], weekNumber: string) => void;
	onCopyWeek: (tempDateItems: ShiftItem[]) => void;
	viewType?: "EDIT" | "CREATE";
}

const WeekItem = ({ dateList, onChangeDateList, onCopyWeek, viewType = "CREATE" }: Props) => {
	const {
		isOpen: isOpenEditWeekDrawer,
		onOpen: onOpenEditWeekDrawer,
		onClose: onCloseEditWeekDrawer,
	} = useDisclosure();

	const [todayExpanded, setTodayExpanded] = useState(false);
	const [tempDateItems, setTempDateItems] = useState<ShiftItem[]>([]);
	const [flexibleStartTimeChecked, setFlexibleStartTimeChecked] = useState(false);
	const [selectedDate, setSelectedDate] = useState<string | undefined>("");

	const weekNumber = dateList && dateList[0].start && getISOWeekString(moment(dateList[0].start).toDate());
	const hasScheduledDates = tempDateItems.find((di) => isValidTime(di.start) && isValidTime(di.end));

	// In EDIT mode, pre-existing booked shifts must not affect the Save button — they are not editable
	// here and may legitimately be shorter than the 4h minimum that's enforced on new shifts.
	const editableItems = tempDateItems.filter(
		(di) => di.tempStatus !== "BOOKED_WITH_CONSULTANT" && di.tempStatus !== "BOOKED_WITHOUT_CONSULTANT",
	);

	const disableSave = !!editableItems.find(
		(di) =>
			(isValidTime(di.start) && !isValidTime(di.end)) ||
			(!isValidTime(di.start) && isValidTime(di.end)) ||
			(isValidTime(di.start) &&
				isValidTime(di.end) &&
				getTimeDiffInHours(getFormatTime(di.start), getFormatTime(di.end)) < 4),
	)
		? true
		: viewType === "CREATE"
			? false
			: !editableItems.find((di) => {
					return (
						isValidTime(di.start) &&
						isValidTime(di.end) &&
						getTimeDiffInHours(getFormatTime(di.start), getFormatTime(di.end)) >= 4
					);
				});

	useEffect(() => {
		dateList && dateList.length > 0 && setTempDateItems(dateList);
	}, [dateList]);

	const shiftDateList = () => {
		const today = moment().startOf("day");
		return tempDateItems.filter((di) => moment(di.start).isSameOrAfter(today));
	};

	useEffect(() => {
		const todayDateItem = dateList.find(
			(di) => isValidTime(di.start) && isValidTime(di.end) && moment().isSame(di.end, "day"),
		);
		if (!!todayDateItem) {
			setTodayExpanded(true);
		}
		if (todayDateItem?.flexible) {
			setFlexibleStartTimeChecked(true);
		}
	}, [dateList]);

	const handleEditWeek = useCallback(
		(dateItem: ShiftItem) => {
			const dateItemData: ShiftItem = dateItem.isExpanded
				? {
						...dateItem,
						break: dateItem.break
							? dateItem.break
							: getTimeDiffInHours(moment(dateItem.start).format("HH:mm"), moment(dateItem.end).format("HH:mm")) >= 5
								? 0.5
								: 0,
						start: moment(dateItem.start).toISOString(),
						end: moment(dateItem.end).toISOString(),
						tempStatus:
							viewType === "CREATE"
								? "DEFAULT"
								: isValidTime(dateItem.start) && isValidTime(dateItem.end)
									? "TEMPORARY_ADDED"
									: "DEFAULT",
					}
				: {
						...dateItem,
						start: moment(dateItem.start).format("YYYY-MM-DD"),
						end: moment(dateItem.end).format("YYYY-MM-DD"),
						break: 0,
						isExpanded: false,
						tempStatus: "DEFAULT",
					};
			if (!dateItem.isExpanded) {
				setSelectedDate(undefined);
			}
			if (moment().format("YYYY-MM-DD") === moment(dateItem.start).format("YYYY-MM-DD"))
				setTodayExpanded(dateItem.isExpanded ? true : false);

			setTempDateItems((prev) => {
				const dateList = [...prev];
				const changedIndex = dateList.findIndex(
					(di) => moment(di.start).format("YYYY-MM-DD") === moment(dateItem.start).format("YYYY-MM-DD"),
				);
				dateList.splice(changedIndex, 1, dateItemData);

				return dateList;
			});
		},
		[dateList, viewType],
	);

	const handleSaveWeek = () => {
		onChangeDateList(tempDateItems, weekNumber);
		onCloseEditWeekDrawer();
	};

	const shouldExpandShift = (item: ShiftItem): boolean => {
		if (item.isExpanded) return true;
		if (selectedDate && selectedDate === getFormatDate(item.start)) return true;
		if (!selectedDate && isValidTime(item.start) && isValidTime(item.end)) return true;
		return false;
	};

	return (
		<>
			<Flex direction={"column"} mb={5}>
				<HStack justifyContent={"space-between"}>
					<Text fontWeight={500} fontSize={14} my={1}>
						{`Vecka ${weekNumber.split("W")[1]}`}
					</Text>
					<HStack justifyContent={"flex-end"}>
						{hasScheduledDates && (
							<Button
								fontSize="14px"
								fontWeight={500}
								color={"#219CD0"}
								variant="link"
								onClick={() => onCopyWeek(tempDateItems)}
							>
								{"Kopiera vecka"}
							</Button>
						)}
						<Button
							isDisabled={getISOWeekString(moment().toDate()) > weekNumber}
							fontSize="14px"
							fontWeight={500}
							color={"#219CD0"}
							variant="link"
							onClick={() => {
								onOpenEditWeekDrawer();
								setSelectedDate(undefined);
							}}
						>
							{hasScheduledDates ? "Redigera pass" : "Lägg till pass"}
						</Button>
					</HStack>
				</HStack>
				<Divider mb={3} />
				<Flex direction={"row"} gap={1} justifyContent={"space-evenly"}>
					{dateList.map((item) => (
						<DateItem
							onEditWeekSchedule={(type, clickedDate) => {
								if (type === "DAY") {
									setSelectedDate(clickedDate);
								}
								onOpenEditWeekDrawer();
							}}
							key={item.start}
							start={item.start}
							end={item.end}
							breakTime={item.break}
							tempStatus={item.tempStatus ? item.tempStatus : "DEFAULT"}
						/>
					))}
				</Flex>
			</Flex>
			{/* edit week drawer */}
			<Drawer
				placement={"right"}
				onClose={() => {
					onCloseEditWeekDrawer();
					setTempDateItems(dateList);
					setSelectedDate(undefined);
				}}
				isOpen={isOpenEditWeekDrawer}
			>
				<DrawerOverlay />
				<DrawerContent borderLeftRadius={"25px"}>
					<DrawerCloseButton />
					<DrawerHeader borderBottomWidth="1px" textAlign={"center"}>
						{`Skapa pass v. ${weekNumber.split("W")[1]}`}
					</DrawerHeader>
					<DrawerBody>
						{shiftDateList().map((item) => (
							<ShiftEditItem
								key={item.start}
								start={item.start}
								end={item.end}
								breakTime={item.break}
								isExpanded={shouldExpandShift(item)}
								onChange={handleEditWeek}
								tempStatus={item.tempStatus ? item.tempStatus : "DEFAULT"}
							/>
						))}
						{todayExpanded && (
							<Box w="100%" my={"auto"} p={3}>
								<Divider my={4} borderColor={"#bfbfbf"} />
								<VStack alignItems={"center"}>
									<HStack
										cursor={"pointer"}
										onClick={() => {
											setFlexibleStartTimeChecked(!flexibleStartTimeChecked);
											const todayDateItem = tempDateItems.find(
												(di) => moment(di.start).format("YYYY-MM-DD") === moment().format("YYYY-MM-DD"),
											);
											!!todayDateItem &&
												handleEditWeek({
													...todayDateItem,
													flexible: !todayDateItem.flexible,
												});
										}}
									>
										{flexibleStartTimeChecked ? <CheckCircleIcon /> : <EmptyWhiteCircle />}
										<Text fontWeight={500} fontSize={14} my={1} color={"#333333"}>
											Flexibel starttid idag (torsdag)
										</Text>
									</HStack>
									<HStack>
										<Text fontWeight={400} fontSize={14} my={1} color={"#999999"}>
											Passet bokas så fort som möjligt (max 1 h senare än angiven starttid).
										</Text>
									</HStack>
								</VStack>
							</Box>
						)}
					</DrawerBody>
					<Button
						variant="solid"
						height="40px"
						borderRadius={"20px"}
						width={"70%"}
						color="white"
						bg={"#2A2A2A"}
						mx={"auto"}
						_hover={{
							backgroundColor: disableSave ? "#9fa0a3" : "#2A2A2A",
						}}
						onClick={handleSaveWeek}
						isDisabled={disableSave}
					>
						Spara
					</Button>
					<DrawerFooter></DrawerFooter>
				</DrawerContent>
			</Drawer>
		</>
	);
};

export default WeekItem;
